/**
 * Multi-track manager: the camera track plus one PropertyAnimTrack per
 * (element, effect). Replaces upstream's single-camera track manager
 * (main.ts registers this one instead).
 *
 * The "active" track is what the key buttons, the ruler and the shortcuts
 * operate on; the track list (ui/track-list.ts) switches it. Tracks are made
 * lazily on first use and dropped explicitly (track.remove), and every key
 * edit goes through the undo history like upstream's.
 */

import type { AnimTrack } from '../anim-track';
import { AnimTrackEditOp } from '../edit-ops';
import { Element, ElementType } from '../element';
import type { Events } from '../events';
import type { Scene } from '../scene';
import type { Splat } from '../splat';
import { createTrackForEffect, effectsForElement, getSceneEffects, type EffectDef } from './effect-catalog';
import type { Model } from './model';
import { hasTracks, tracksOf, type TrackInfo } from './tracks';

export type TrackTarget = { element: Element | null; effectId: string | null };

const registerOlloTrackManager = (events: Events) => {
    // the scene arrives later than this registration (main.ts creates the UI
    // first); resolve it on demand
    const sceneOf = (): Scene | null => (events.functions.has('ollo.scene') ? (events.invoke('ollo.scene') as Scene) : null);

    let activeElement: Element | null = null;
    let activeEffectId: string | null = null;
    const sceneTracks = new Map<string, AnimTrack>();

    const effectsFor = (element: Element | null): EffectDef[] => {
        const scene = sceneOf();
        if (!scene) return [];
        return element ? effectsForElement(element, scene) : getSceneEffects(scene);
    };

    const isCamera = (effectId: string | null) => !effectId || effectId === 'camera-pose';

    const resolveTrack = (element: Element | null, effectId: string | null, create = true): AnimTrack | null => {
        if (isCamera(effectId)) return (events.invoke('camera.animTrack') as AnimTrack) ?? null;
        const map = element ? tracksOf(element) : sceneTracks;
        const existing = map.get(effectId);
        if (existing || !create) return existing ?? null;
        const scene = sceneOf();
        if (!scene) return null;
        const def = effectsFor(element).find(d => d.id === effectId);
        if (!def) return null;
        const fakeEl = element ?? ({ uid: 0, type: 'scene' as ElementType } as Element);
        const track = createTrackForEffect(events, scene, fakeEl, def);
        map.set(effectId, track);
        events.fire('track.added', { element, effectId });
        return track;
    };

    const getActiveTrack = () => resolveTrack(activeElement, activeEffectId);

    const setActive = (element: Element | null, effectId: string | null) => {
        if (element === activeElement && effectId === activeEffectId) return;
        activeElement = element;
        activeEffectId = effectId;
        events.fire('track.activeChanged', { element, effectId });
    };

    const nameOf = (el: Element): string => (el as Splat | Model).name ?? `${el.type} ${el.uid}`;

    events.function('track.activeElement', () => activeElement);
    events.function('track.activeEffectId', () => activeEffectId);
    events.function('track.activeTarget', (): TrackTarget => ({ element: activeElement, effectId: activeEffectId }));
    events.function('track.availableEffects', () => effectsFor(activeElement));
    events.function('track.effectsFor', (element: Element | null) => effectsFor(element));
    events.function('track.activeDef', (): EffectDef | null => {
        if (isCamera(activeEffectId)) return null;
        return effectsFor(activeElement).find(d => d.id === activeEffectId) ?? null;
    });

    /** Every existing track, camera first, for the track list. */
    events.function('tracks.discover', (): TrackInfo[] => {
        const out: TrackInfo[] = [];
        const cameraTrack = events.invoke('camera.animTrack') as AnimTrack | null;
        if (cameraTrack) {
            out.push({ id: 'camera-pose', kind: 'camera', elementName: 'Camera', effectName: 'Pose', element: null, effectId: 'camera-pose', track: cameraTrack });
        }
        const scene = sceneOf();
        if (scene) {
            for (const el of scene.elements) {
                if (!hasTracks(el)) continue;
                const defs = effectsForElement(el, scene);
                for (const [effectId, track] of tracksOf(el)) {
                    out.push({
                        id: `${el.type}:${el.uid}:${effectId}`,
                        kind: 'effect',
                        elementName: nameOf(el),
                        effectName: defs.find(d => d.id === effectId)?.name ?? effectId,
                        element: el,
                        effectId,
                        track
                    });
                }
            }
        }
        return out;
    });

    /** Elements a track can be added to. */
    events.function('tracks.targets', (): Element[] => {
        const scene = sceneOf();
        if (!scene) return [];
        return scene.elements.filter(e => e.type === ElementType.splat || e.type === ElementType.model);
    });

    events.function('tracks.ensure', (target: TrackTarget) => resolveTrack(target.element, target.effectId));

    events.on('track.setActive', (target: TrackTarget) => setActive(target.element, target.effectId));

    events.on('track.remove', (target: TrackTarget) => {
        if (isCamera(target.effectId)) return;
        const map = target.element ? tracksOf(target.element) : sceneTracks;
        const track = map.get(target.effectId);
        if (!track) return;
        track.clear();
        map.delete(target.effectId);
        events.fire('track.removed', target);
        if (target.element === activeElement && target.effectId === activeEffectId) {
            setActive(activeElement, null);
        }
    });

    // upstream's key API, now against whichever track is active
    events.function('track.keys', () => getActiveTrack()?.keys ?? []);

    const trackEdit = (name: string, editFn: (track: AnimTrack) => boolean) => {
        const track = getActiveTrack();
        if (!track) return;
        const before = track.snapshot();
        if (!editFn(track)) return;
        const after = track.snapshot();
        events.fire('edit.add', new AnimTrackEditOp(name, track, before, after), true);
    };

    events.on('track.addKey', (frame?: number) => {
        const keyFrame = frame ?? (events.invoke('timeline.frame') as number);
        trackEdit('addKey', track => track.addKey(keyFrame));
    });
    events.on('track.removeKey', (frame?: number) => {
        const keyFrame = frame ?? (events.invoke('timeline.frame') as number);
        trackEdit('removeKey', track => track.removeKey(keyFrame));
    });
    events.on('track.moveKey', (fromFrame: number, toFrame: number) => {
        trackEdit('moveKey', track => track.moveKey(fromFrame, toFrame));
    });
    events.on('track.copyKey', (fromFrame: number, toFrame: number) => {
        trackEdit('copyKey', track => track.copyKey(fromFrame, toFrame));
    });

    // selecting an element in the scene list makes its tracks the natural
    // target; the effect stays if that element has it too
    events.on('selection.changed', (selection: Element | null | undefined) => {
        const el = selection ?? null;
        if (el === activeElement) return;
        const effectId = activeEffectId && effectsFor(el).find(d => d.id === activeEffectId) ? activeEffectId : null;
        setActive(el, effectId);
    });

    events.on('scene.elementRemoved', (el: Element) => {
        if (el === activeElement) setActive(null, null);
    });

    events.on('scene.clear', () => {
        sceneTracks.clear();
        setActive(null, null);
    });

    // ── document ─────────────────────────────────────────────────────
    const captureConfig = (el: Element | null, def: EffectDef): Record<string, number[]> => {
        const scene = sceneOf();
        const cfg: Record<string, number[]> = {};
        if (!def.config?.length || !scene) return cfg;
        for (const param of def.config) {
            try {
                cfg[param.label] = param.get(el ?? ({ uid: 0, type: 'scene' as ElementType } as Element), scene);
            } catch { /* a knob that can't be read is simply not stored */ }
        }
        return cfg;
    };

    events.function('tracks.serialize', () => {
        const scene = sceneOf();
        if (!scene) return [];
        const out: unknown[] = [];
        for (const el of scene.elements) {
            if (!hasTracks(el)) continue;
            const defs = effectsForElement(el, scene);
            for (const [effectId, track] of tracksOf(el)) {
                const def = defs.find(d => d.id === effectId);
                if (!def) continue;
                out.push({ elementUid: el.uid, elementName: nameOf(el), elementType: el.type, effectId, keys: track.snapshot(), config: captureConfig(el, def) });
            }
        }
        for (const [effectId, track] of sceneTracks) {
            const def = getSceneEffects(scene).find(d => d.id === effectId);
            if (!def) continue;
            out.push({ elementUid: 0, elementType: 'scene', effectId, keys: track.snapshot(), config: captureConfig(null, def) });
        }
        return out;
    });

    events.function('tracks.deserialize', (data: any[]) => {
        const scene = sceneOf();
        if (!scene || !Array.isArray(data) || data.length === 0) return;
        const byUid = new Map<number, Element>();
        const byName = new Map<string, Element>();
        for (const el of scene.elements) {
            byUid.set(el.uid, el);
            byName.set(nameOf(el), el);
        }
        for (const entry of data) {
            if (!entry || typeof entry !== 'object' || typeof entry.effectId !== 'string') continue;
            const { elementUid, elementName, effectId, keys, config } = entry;
            // uids are per session; the name is what survives a reload
            const el = elementUid === 0 ? null : (byName.get(elementName) ?? byUid.get(elementUid) ?? null);
            if (elementUid !== 0 && !el) continue;
            const track = resolveTrack(el, effectId);
            if (!track) continue;
            const def = effectsFor(el).find(d => d.id === effectId);
            if (config && typeof config === 'object' && def?.config?.length) {
                for (const param of def.config) {
                    const v = config[param.label];
                    if (Array.isArray(v)) {
                        try {
                            param.set(el ?? ({ uid: 0, type: 'scene' as ElementType } as Element), scene, v);
                        } catch { /* skip */ }
                    }
                }
            }
            if (Array.isArray(keys) && keys.length > 0) {
                track.restore(keys);
                def?.onActivate?.(el ?? ({ uid: 0, type: 'scene' as ElementType } as Element), scene);
            }
        }
        events.fire('track.keysLoaded');
    });
};

export { registerOlloTrackManager };
